// "What the user sees" — the data model behind UserFacingPreviewWidget.
//
// Pure logic, no React, and no runtime import from anywhere else: the file is
// loaded as-is by `node --test` (model.test.mjs), where Node strips the types
// but does not resolve extensionless or aliased specifiers. Keep every import
// here `import type`.
//
// Two questions this answers for one user_entry:
//   1. Which public surface did the reporter use (detectSurface) — from
//      user_entries.source (operator-editable) with raw_entries.original_payload
//      as the fallback witness.
//   2. What did that surface render (selectAnswer) — never from the row's
//      is_scam/scam_claims (Python, Hono and operators all overwrite those) but
//      from the pipeline step whose json_info IS the object the client got:
//      classify_success (postInsertion.ts writes `data` to the step and returns
//      the same `data`), multibanco-validate (the validator's synchronous
//      answer), and the retry / manual-update steps a replay view reads later.

import type { EntryOriginalPayloadHints, PipelineStep, TranslationData } from '../../Entries/entryTypes'

// ─── Surfaces ─────────────────────────────────────────────────────────────────

export type PublicSurface =
  | 'bon-chat'           // Burla ou Não webapp: result bubbles right after submit
  | 'bon-my-submissions' // Burla ou Não webapp: account › my submissions replay
  | 'elegitimo'          // É Legítimo: verdict box (polls /elegitimo/status)
  | 'validator'          // Multibanco / IBAN / Is It Scam validators: "Resultado" list
  | 'domain-checker'     // project-domain: verdict box
  | 'feed-card'          // public feed card (Burla ou Não feed, É Legítimo lists)
  | 'none'               // machine sources: nobody received an answer

export type SurfaceInfo = {
  id: Exclude<PublicSurface, 'none'>
  /** Tab label. */
  label: string
  /** Long name for tooltips and the footer. */
  title: string
  /** The upstream renderer(s) this tab mirrors — where to look when the real app changes. */
  upstream: string[]
  /** True when the real surface re-reads the entry later (so "Agora" is what a
   * visitor sees today); false when it answered once at submit time and
   * "Agora" is a hypothetical "if they submitted it again now". */
  liveNow: boolean
}

export const SURFACES: SurfaceInfo[] = [
  {
    id: 'bon-chat',
    label: 'Burla ou Não',
    title: 'Burla ou Não · resposta à submissão',
    upstream: ['fraud-mono-repo/webapp/src/components/post/Card1Chat.tsx (handleSubmit, ResultView)'],
    liveNow: false,
  },
  {
    id: 'bon-my-submissions',
    label: 'Minhas partilhas',
    title: 'Burla ou Não · conta › minhas partilhas',
    upstream: ['fraud-mono-repo/webapp/src/components/account/SubmissionChatView.tsx', 'backoffice Entries/SubmissionChatView.tsx'],
    liveNow: true,
  },
  {
    id: 'elegitimo',
    label: 'É Legítimo',
    title: 'É Legítimo · caixa de veredicto',
    upstream: ['fraud-mono-repo/project-elegitimo/src/app/page.tsx (formatBackendVerdict)', 'fraud-mono-repo/backend/routes/subapps/elegitimo.ts (/elegitimo/status)'],
    liveNow: false,
  },
  {
    id: 'validator',
    label: 'Validador',
    title: 'Validador Multibanco / IBAN / Is It Scam · "Resultado"',
    upstream: [
      'fraud-mono-repo/multibanco-validator/src/components/PaymentValidatorForm.tsx (messagesFromBackendResult)',
      'fraud-mono-repo/project-iban/src/components/PaymentValidatorForm.tsx',
      'fraud-mono-repo/project-isitscam/src/components/PaymentValidatorForm.tsx',
    ],
    liveNow: false,
  },
  {
    id: 'domain-checker',
    label: 'Domínio',
    title: 'Verificador de domínio · caixa de veredicto',
    upstream: ['fraud-mono-repo/project-domain/src/components/DomainCheckForm.tsx (formatVerdict)'],
    liveNow: false,
  },
  {
    id: 'feed-card',
    label: 'Feed',
    title: 'Feed público · cartão',
    upstream: ['fraud-mono-repo/webapp AltBentoGridItem', 'backoffice Entries/FeedPreviewCard.tsx'],
    liveNow: true,
  },
]

export function surfaceInfo(id: PublicSurface): SurfaceInfo | undefined {
  return SURFACES.find((s) => s.id === id)
}

// ─── Surface detection ────────────────────────────────────────────────────────

export type SurfaceDetection = {
  surface: PublicSurface
  /** 'exact' = the source names one public app; 'default' = bon/empty/unknown,
   * which the webapp, the gov mockup and the CTT mockup all share. */
  confidence: 'exact' | 'default'
  /** Human-readable reasons, printed in the widget footer. */
  evidence: string[]
  /** Sub-brand of a shared renderer (the three validators share one). */
  variant?: string
}

// user_entries.source values → surface. The right-hand column is what the
// Hono submit routes stamp (routes/posts/post.ts defaults to 'bon'; the
// public apps send their own; postPythonBackend.ts stamps the machine ones).
const EXACT_SOURCE: Record<string, PublicSurface> = {
  'e-legitimo': 'elegitimo',
  'multibanco-validator': 'validator',
  'iban-validator': 'validator',
  'boilerplate-project': 'validator', // project-isitscam never changed the boilerplate's tag
  'project-domain': 'domain-checker',
  twitter: 'none',
  auto_domains: 'none',
  'ligaram sites': 'none',
  ctt_trustfence_email: 'none',
  'web-form-mirror': 'none',
}

const VALIDATOR_VARIANT: Record<string, string> = {
  'multibanco-validator': 'Multibanco',
  'iban-validator': 'IBAN',
  'boilerplate-project': 'Is It Scam',
}

/** Sources that have no end user at all — the footer explains instead of rendering. */
export function isMachineSource(source: string | null | undefined): boolean {
  const key = (source ?? '').trim().toLowerCase()
  return EXACT_SOURCE[key] === 'none' || key.startsWith('ligaram')
}

export function detectSurface(
  source: string | null | undefined,
  payload: EntryOriginalPayloadHints | null | undefined,
): SurfaceDetection {
  const evidence: string[] = []
  const candidates: Array<[string, string]> = []
  const fromRow = (source ?? '').trim()
  const fromPayload = typeof payload?.source === 'string' ? payload.source.trim() : ''
  if (fromRow) candidates.push(['source', fromRow])
  if (fromPayload && fromPayload !== fromRow) candidates.push(['payload.source', fromPayload])

  for (const [where, value] of candidates) {
    const key = value.toLowerCase()
    const hit = EXACT_SOURCE[key] ?? (key.startsWith('ligaram') ? 'none' : undefined)
    if (hit) {
      evidence.push(`${where} = ${value}`)
      return { surface: hit, confidence: 'exact', evidence, variant: VALIDATOR_VARIANT[key] }
    }
  }

  if (fromRow) evidence.push(`source = ${fromRow}` + (fromRow.toLowerCase() === 'bon' ? ' (webapp default; gov/CTT mockups share it)' : ' (not a known public app)'))
  else evidence.push('source is empty (webapp before the "bon" default)')
  if (payload?.turnstile_key === 'extension') evidence.push('payload.turnstile_key = extension')
  return { surface: 'bon-chat', confidence: 'default', evidence }
}

// raw_entries.original_payload is the submitted body minus the image, the
// Turnstile token and the fingerprint (backend/queries/posts/post.ts). The
// unified view exposes it as raw_entry_original_payload; the nested
// /userentrydetail shape as raw_entries.original_payload. Either may be a
// JSON string or an object depending on the path it travelled.
export function readOriginalPayload(raw: Record<string, unknown> | null | undefined): EntryOriginalPayloadHints | null {
  const value = raw?.original_payload ?? raw?.raw_entry_original_payload
  if (value == null) return null
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? (parsed as EntryOriginalPayloadHints) : null
    } catch {
      return null
    }
  }
  return typeof value === 'object' ? (value as EntryOriginalPayloadHints) : null
}

// ─── Pipeline step kinds ──────────────────────────────────────────────────────

/** A classification the client could have been shown (Hono or Python wrote the result). */
export const isSuccessStep = (step: string | undefined): boolean =>
  step === 'classify_success' || step === 'classify_retry_success' || !!step?.startsWith('classify_manual_retry_success')

/** An operator edit of the public text (/updateentryclassification). */
export const isManualUpdateStep = (step: string | undefined): boolean => !!step?.startsWith('classify_manual_update')

/** The validator's synchronous bank-lookup answer (/mbvalidate). */
export const isValidateStep = (step: string | undefined): boolean => step === 'multibanco-validate'

export const isFailureStep = (step: string | undefined): boolean =>
  step === 'classify_failed' ||
  step === 'classify_failed_frombackend' ||
  step === 'classify_failed_frompython' ||
  step === 'classify_retry_failed' ||
  step === 'multibanco_validate_failed' ||
  !!step?.startsWith('classify_manual_retry_failed')

export const isPendingStep = (step: string | undefined): boolean =>
  step === 'classify_started' || step === 'classify' || !!step?.startsWith('classify_manual_retry_started')

/** The "latest verdict" rule every replay view uses (getEntryIsScam, SubmissionChatView). */
export const isReplayStep = (step: string | undefined): boolean => isSuccessStep(step) || isManualUpdateStep(step)

// ─── Answer selection ─────────────────────────────────────────────────────────

export type AnswerMoment = 'submit' | 'now'

export type AnswerKind =
  | 'answer'  // a result object the surface rendered
  | 'pending' // the surface showed its "still analysing / received" copy
  | 'failed'  // the surface showed its failure copy (only surfaces that have one)
  | 'call'    // Burla ou Não: calls are never classified, the chat just says thanks
  | 'none'    // machine source: nobody saw anything

export type PublicAnswer = {
  kind: AnswerKind
  surface: PublicSurface
  moment: AnswerMoment
  /** The step whose json_info is rendered (absent for pending/failed/call/none). */
  step?: PipelineStep
  /** step.json_info verbatim — the ported formatters consume this wire shape. */
  json: Record<string, unknown>
  /** Whether the submitting client could have received this result inline
   * (within SYNC_WINDOW_S of the raw entry, not deferred). null = not applicable. */
  deliveredSync: boolean | null
  /** Seconds between raw_entries.created_at and the step; null when unknown. */
  secondsAfterSubmit: number | null
  /** Footnotes for the provenance strip: heuristics applied, product gaps noticed. */
  notes: string[]
  isScam: boolean | null | undefined
  translation?: TranslationData
  hidden: boolean
  hideClassification: boolean
}

/** How long after the raw entry a classify_success can still have been the
 * synchronous answer. Hono awaits e32 inline (SHOW_SUBMIT_RESULT=1) and the
 * webapp/É Legítimo keep their spinner for that long; a result landing later
 * came from the background retry the client never saw. */
export const SYNC_WINDOW_S = 90

/** É Legítimo polls /elegitimo/status for at most this long (POLL_MAX_MS). */
export const ELEGITIMO_TIMEOUT_S = 4 * 60

function parseTs(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null
  let iso = value.trim().replace(' ', 'T')
  // Postgres '+00' / '+01' offsets are not valid for Date; pad to '+00:00'.
  if (/[+-]\d{2}$/.test(iso)) iso = `${iso}:00`
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? null : t
}

const stepTime = (s: PipelineStep): number => parseTs(s.created_at) ?? 0

export function sortStepsAsc(steps: PipelineStep[]): PipelineStep[] {
  return [...steps].sort((a, b) => stepTime(a) - stepTime(b))
}

/** Steps up to and including `until` — what a replay view had to work with at that moment. */
export function stepsUpTo(steps: PipelineStep[], until: PipelineStep | undefined): PipelineStep[] {
  if (!until) return steps
  const limit = stepTime(until)
  return steps.filter((s) => stepTime(s) <= limit)
}

export function secondsBetween(raw: Record<string, unknown> | null | undefined, step: PipelineStep | undefined): number | null {
  const a = parseTs(raw?.created_at)
  const b = step ? parseTs(step.created_at) : null
  if (a == null || b == null) return null
  return Math.round((b - a) / 1000)
}

const hasError = (s: PipelineStep): boolean => {
  const err = s.json_info?.error
  return err !== undefined && err !== null && err !== false && err !== ''
}

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {})

function fromStep(
  kind: AnswerKind,
  surface: PublicSurface,
  moment: AnswerMoment,
  step: PipelineStep | undefined,
  raw: Record<string, unknown> | null | undefined,
  payload: EntryOriginalPayloadHints | null | undefined,
  notes: string[],
): PublicAnswer {
  const json = asRecord(step?.json_info)
  const secs = secondsBetween(raw, step)
  const deferred = payload?.simplePost === true || payload?.deferClassification === true
  const deliveredSync = step ? !deferred && (secs == null || secs <= SYNC_WINDOW_S) : null
  const isScamRaw = json.is_scam
  return {
    kind,
    surface,
    moment,
    step,
    json,
    deliveredSync,
    secondsAfterSubmit: secs,
    notes,
    isScam: typeof isScamRaw === 'boolean' ? isScamRaw : isScamRaw === null ? null : undefined,
    translation: json.translation && typeof json.translation === 'object' ? (json.translation as TranslationData) : undefined,
    hidden: json.hidden === true,
    hideClassification: json.hide_classification === true,
  }
}

/**
 * Pick the step a surface rendered for this entry at the given moment.
 *
 * submit = the answer the reporter got on the spot; now = what that surface
 * (or its replay view) shows if someone looks today, after retries and
 * operator edits. Rules per surface are documented inline; they follow the
 * upstream code paths named in SURFACES[].upstream.
 */
export function selectAnswer(
  steps: PipelineStep[],
  raw: Record<string, unknown> | null | undefined,
  payload: EntryOriginalPayloadHints | null | undefined,
  surface: PublicSurface,
  moment: AnswerMoment,
): PublicAnswer {
  const notes: string[] = []
  const pick = (kind: AnswerKind, step?: PipelineStep) => fromStep(kind, surface, moment, step, raw, payload, notes)

  if (surface === 'none') {
    notes.push('Fonte automática: nenhum utilizador recebeu resposta a esta entrada.')
    return pick('none')
  }

  const asc = sortStepsAsc(steps)
  const successes = asc.filter((s) => isSuccessStep(s.step))
  const manual = asc.filter((s) => isManualUpdateStep(s.step))
  const validates = asc.filter((s) => isValidateStep(s.step))
  const failures = asc.filter((s) => isFailureStep(s.step))
  const deferred = payload?.simplePost === true || payload?.deferClassification === true
  const isCall = raw?.post_type === 'call'

  if (moment === 'now') {
    if (surface === 'elegitimo') {
      // /elegitimo/status: the LAST classify_success whose json has no error;
      // manual updates and retry-success variants are invisible to it.
      const last = [...successes].reverse().find((s) => s.step === 'classify_success' && !hasError(s))
      if (manual.length > 0) notes.push(`É Legítimo lê apenas classify_success: ${manual.length} edição(ões) manual(is) não aparecem lá.`)
      if (!last) {
        if (failures.length > 0 && successes.length === 0) return pick('failed')
        notes.push('Sem classify_success legível: a página continuaria "Análise em curso".')
        return pick('pending')
      }
      return pick('answer', last)
    }
    const replay = asc.filter((s) => isReplayStep(s.step))
    const latest = replay[replay.length - 1]
    if (!latest) {
      if (surface === 'validator' && validates.length > 0) {
        notes.push('Sem classificação posterior: o único resultado é a validação bancária.')
        return pick('answer', validates[validates.length - 1])
      }
      if (failures.length > 0) return pick('failed')
      return pick('pending')
    }
    if (!surfaceInfo(surface)?.liveNow) notes.push('Esta superfície responde uma só vez: "Agora" mostra o que diria a uma nova submissão.')
    return pick('answer', latest)
  }

  // moment === 'submit'
  switch (surface) {
    case 'validator': {
      // The validator's inline answer is the /mbvalidate response; the full
      // classification runs afterwards in the background (postInsertion.ts).
      if (validates[0]) return pick('answer', validates[0])
      if (successes[0]) {
        notes.push('Sem passo multibanco-validate: a mostrar a primeira classificação.')
        return pick('answer', successes[0])
      }
      if (failures.length > 0) return pick('failed')
      return pick('pending')
    }
    case 'elegitimo': {
      // Polls every 3 s for up to 4 min; done = first classify_success
      // without error; failed = a failure step with no success; else timeout.
      const first = successes.find((s) => s.step === 'classify_success' && !hasError(s))
      if (first) {
        const secs = secondsBetween(raw, first)
        if (secs != null && secs > ELEGITIMO_TIMEOUT_S) {
          notes.push(`O veredicto chegou +${secs}s depois da submissão, fora da janela de 4 min: a página mostrou o texto de timeout.`)
          return pick('pending')
        }
        return pick('answer', first)
      }
      if (failures.length > 0) return pick('failed')
      return pick('pending')
    }
    case 'bon-chat':
    case 'domain-checker': {
      if (surface === 'bon-chat' && isCall) {
        notes.push('Chamadas nunca são classificadas: o chat agradece e termina.')
        return pick('call')
      }
      if (deferred) {
        notes.push('O pedido levava simplePost/deferClassification: o cliente recebeu classificationPending.')
        return pick('pending')
      }
      const first = successes[0]
      if (!first) {
        if (failures.length > 0) notes.push('A classificação falhou; o cliente recebeu classificationPending.')
        return pick('pending')
      }
      const secs = secondsBetween(raw, first)
      if (secs != null && secs > SYNC_WINDOW_S) {
        notes.push(`O primeiro veredicto chegou +${secs}s depois da submissão, além da janela síncrona de ${SYNC_WINDOW_S}s: o cliente recebeu classificationPending.`)
        return pick('pending')
      }
      return pick('answer', first)
    }
    case 'bon-my-submissions':
    case 'feed-card': {
      // The replay/feed right after submission: whatever the first result was.
      const first = successes[0] ?? validates[0]
      if (!first) return pick(failures.length > 0 ? 'failed' : 'pending')
      return pick('answer', first)
    }
    default:
      return pick('pending')
  }
}

/** The steps a replay view (SubmissionChatView) should be handed for this answer. */
export function stepsForAnswer(steps: PipelineStep[], answer: PublicAnswer): PipelineStep[] {
  return answer.moment === 'submit' ? stepsUpTo(steps, answer.step) : steps
}
